import { IReport, IReportField, IReportPreferencesItem } from "@/api/clients";
import { cloneDeep, groupBy } from "es-toolkit";

export interface IEnhancedReportField extends IReportField {
  secondaryFields: [string, IReportField][];
}

class ReportFieldsUtils {
  static groupFields(
    fields: Record<string, IReportField>
  ): Record<
    IReportPreferencesItem["category"],
    Record<string, [string, IEnhancedReportField][]>
  > {
    const fieldsEntries = Object.entries(fields);

    const byCategory: Record<string, [string, IReportField][]> = groupBy(
      fieldsEntries,
      ([_, field]) => field.metadata?.category ?? ""
    );

    const byGroup = Object.keys(byCategory).reduce(
      (acc, category) => ({
        ...acc,
        [category]: groupBy(
          ReportFieldsUtils.sortFieldEntries(
            ReportFieldsUtils.getNestedFieldFromEntries(byCategory[category])
          ),
          ([_, field]) => field.metadata?.group ?? ""
        ),
      }),
      {}
    );

    return byGroup as any;
  }

  /**
   * Given a group and a data object, this function will return the key of the field in the data object
   * that is in the same group and is included in the sorting fields list.
   * If there is no match for the group in the sorting fields list, the function will return undefined.
   * @param group The group to search in.
   * @param data The data object to search in.
   * @returns The key of the field in the data object that is in the same group and is included in the sorting fields list.
   */
  static getGroupSortingField(
    groupSortByFieldsValues: Record<string, string[]>,
    group: string,
    data: Record<string, any>
  ) {
    const sortingFields =
      groupSortByFieldsValues[group as keyof typeof groupSortByFieldsValues] ??
      [];

    const key = Object.keys(data).find((_key) => sortingFields.includes(_key));

    return key;
  }

  /**
   * Given a report and the updated fields values, this function will sort the fields values
   * by the values in the fields that are in the same group as the one being updated.
   * The function will only consider the fields that have the same group as the one being updated
   * and the ones that are in the sorting fields list.
   *
   * The function will return a tuple with the updated report and the updates made to the fields values.
   *
   * The sorting will be done by the order of the field metadata and then by the values.
   * The values will be sorted in descending order.
   *
   * The function will mutate the report fields to mimic adding new values with the database structure.
   *
   * The function will return an empty updates object if there is no match for the updateKey in the report fields.
   *
   * @param report The report to update.
   * @param updatedFieldsValues The new values for the fields.
   * @param group The group to sort the fields by.
   * @param updateKey The key of the field being updated.
   * @param value The new value of the field being updated.
   * @returns A tuple with the updated report and the updates made to the fields values.
   */
  static sortReportFieldsGroup(
    groupSortByFieldsValues: Record<string, string[]>,
    report: IReport,
    updatedFieldsValues: Record<string, any>,
    group: string,
    updateKey: string,
    value: any
  ): [IReport, any] {
    const allowedTypes = ["int", "float"];
    const sortingFields =
      groupSortByFieldsValues[group as keyof typeof groupSortByFieldsValues] ??
      [];

    const reportFieldsValues: Record<string, any> = Object.entries(
      report?.fields ?? {}
    ).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value.values.at(-1)?.value,
      }),
      {}
    );

    const mainField = report.fields?.[updateKey]; // * GETTING THE MAIN FIELD

    if (!mainField) return [report, {}]; // * IF THERE IS NO MATCH RETURN THE REPORT AND EMPTY UPDATES

    const entries = Object.entries(report.fields ?? {});

    // * GETTING ALL THE FIELDS IN THE SAME GROUP AS THE MAIN FIELD
    const groupFields = entries.filter(
      ([key, field]) =>
        field.metadata?.group === mainField.metadata?.group &&
        allowedTypes.includes(field?.metadata?.type ?? "") &&
        sortingFields.includes(key)
    );

    // * ORDERING THE FIELDS BY THEIR ORDER
    const groupFieldsByOrder = groupFields.sort(([, aField], [, bField]) => {
      const aOrder = aField.metadata?.order ?? 0;
      const bOrder = bField.metadata?.order ?? 0;

      return aOrder - bOrder;
    });
    // * ORDERING NOW BY VALUES
    const orderByValues = groupFields
      .map(([key, field]) => {
        if (key == updateKey) return [key, value];
        if (
          updatedFieldsValues[key] !== undefined &&
          updatedFieldsValues[key] !== null
        )
          return [key, updatedFieldsValues[key]];
        return [key, field.values?.at(-1)?.value ?? undefined];
      })
      .sort(([, valA], [, valB]) => {
        const aValue = Number(valA) ?? 0;
        const bValue = Number(valB) ?? 0;

        return bValue - aValue;
      });

    // * NOW FOREACH FIELD ORDERED BY ORDER PROPERTY, FIND THE CORRESPIONDING VALUE BY INDEX
    const updates = groupFieldsByOrder.reduce((acc, [key], crrIndex) => {
      const mainField = Object.keys(report.fields ?? {}).find((fieldKey) =>
        (report?.fields?.[fieldKey]?.metadata?.secondary_fields ?? []).includes(
          key
        )
      );

      // * Getting Main FieldSubFields for the current field key (BY THE ORDER)
      const mainFieldSubfields =
        report.fields?.[mainField ?? ""]?.metadata?.secondary_fields ?? [];

      // * Getting all the fields to update (mainfield & secondary fields)
      const updatesA = [mainField ?? "", ...mainFieldSubfields].map(
        (fieldKey) => [
          fieldKey,
          updatedFieldsValues[fieldKey] ?? reportFieldsValues[fieldKey],
        ]
      );

      // * Getting the values now by the order
      const valueSortByValue = orderByValues[crrIndex];

      // * LOOKING FOR ALL THE FIELDS USING THE KEY OF THE VALUE SORTED BY VALUES ( VALUES TO MERGE )
      const mainFieldB = Object.keys(report.fields ?? {}).find((fieldKey) =>
        (report?.fields?.[fieldKey]?.metadata?.secondary_fields ?? []).includes(
          valueSortByValue[0]
        )
      );
      // * GETTING ALL THE SECONDARY FIELDS FOR THE MAIN FIELD GETTED BY SORTED VALUES
      const mainFieldSubfieldsB =
        report.fields?.[mainFieldB ?? ""]?.metadata?.secondary_fields ?? [];
      // * Getting all the fields to update (mainfield & secondary fields)
      const updatesB = [mainFieldB ?? "", ...mainFieldSubfieldsB].map(
        (fieldKey) => [
          fieldKey,
          updatedFieldsValues[fieldKey] ?? reportFieldsValues[fieldKey],
        ]
      );
      // * Merging the values
      const allUpdates = updatesA.reduce((acc, [key], crrIndex) => {
        return {
          ...acc,
          [key]: updatesB[crrIndex][1],
        };
      }, {});

      return {
        ...acc,
        ...allUpdates,
      };
    }, {});

    const reportFields = { ...report.fields };

    // * MUTATING THE REPORT FIELDS TO MIMIC ADDING NEW VALUES WITH THE DATABASE STRUCTURE
    Object.entries(updates).forEach(([key, value]) => {
      const values: IReportField["values"] = reportFields[key].values ?? [];
      const lastValue = values.at(-1) ?? {
        metadata: { timestamp: 0, author: null, description: null },
      };

      reportFields[key].values.push({
        ...lastValue,
        value: value as any,
      });
    });

    report.fields = reportFields;

    return [cloneDeep(report), updates];
  }

  static unflattenFields(
    fields: [string, IEnhancedReportField][]
  ): [string, IReportField][] {
    return fields.flatMap(
      ([mainFieldKey, { secondaryFields, ...mainField }]) => {
        return [[mainFieldKey, mainField], ...secondaryFields];
      }
    );
  }

  /**
   * Sorts an array of field entries by their key, with the sorting algorithm
   * based on the first number found in the key.
   *
   * For example, the following entries will be sorted in the following order:
   *
   * - `field1`
   * - `field2`
   * - `field_1`
   * - `field_10`
   * - `field_2`
   *
   * @param fields The array of field entries to sort.
   * @returns The sorted array of field entries.
   */
  static sortFieldEntries(fields: [string, IReportField][]) {
    return fields.sort(([a, { metadata: aMetadata }], [b, { metadata: bMetadata }]) => {
      const aNumber = Number(a.match(/\d+/)?.[0]) || 0;
      const bNumber = Number(b.match(/\d+/)?.[0]) || 0;

      const aOrder = aMetadata?.order ?? 0;
      const bOrder = bMetadata?.order ?? 0;

      if(aMetadata?.order || bMetadata?.order) {
        return aOrder - bOrder
      }
      return aNumber - bNumber;
    });
  }

  static sortGroups(groups: Record<string, [string, IEnhancedReportField][]> = {}) {
    const _groups = Object.entries(groups);

    return _groups.sort(
      ([nameA, aFields], [nameB, bFields]) => {
        // Calculate the count of is_editable fields for each group
        // const aIsEditable = aFields.filter(
        //   ([_, field]) => field.metadata?.is_editable
        // ).length;

        // const bIsEditable = bFields.filter(
        //   ([_, field]) => field.metadata?.is_editable
        // ).length;

        // // First sort by the count of editable fields (descending)
        // if (bIsEditable !== aIsEditable) {
        //   return bIsEditable - aIsEditable;
        // }

        // If counts are the same, sort alphabetically by name (ascending)
        return nameA.localeCompare(nameB);
      }
    );
  }

  static getNestedFieldFromEntries(
    fields: [string, IReportField][]
  ): [string, IEnhancedReportField][] {
    const fieldsHash = Object.fromEntries(fields);

    const mainFields = fields.filter(
      ([_, field]) => !field.metadata?.secondary_field
    );

    return mainFields.map(([key, field]) => {
      const _secondaryFields = field.metadata?.secondary_fields ?? [];

      const secondaryFields: [string, IReportField][] = _secondaryFields.map(
        (secondaryFieldName) => {
          const secondaryField = fieldsHash[secondaryFieldName];
          return [secondaryFieldName, secondaryField];
        }
      );

      return [
        key,
        {
          ...field,
          secondaryFields,
        },
      ];
    });
  }
}

export { ReportFieldsUtils };
